import { flushOutbox, type FlushReport } from './sync.js';

/**
 * Service-worker registration and Background Sync.
 *
 * Deliberately not registered in development: a cached shell during a hot-reload
 * session produces the single most confusing class of bug there is, where a fix
 * is on disk and in the browser's cache at the same time.
 *
 * ADR-0013 again: the worker's URL is built from the configured base path rather
 * than assumed to be the origin root, because this build is served under a
 * versioned path prefix.
 */

const BASE = import.meta.env.BASE_URL || '/';
const SYNC_TAG = 'qms360-outbox';

type SyncCapableRegistration = ServiceWorkerRegistration & {
  sync?: { register(tag: string): Promise<void> };
};

export interface RegisterOptions {
  /** Called when the worker asks the page to drain its queue. */
  onFlushed?: (report: FlushReport) => void;
  /** Escape hatch for a local session that needs the worker on. */
  force?: boolean;
}

export function registerServiceWorker(options: RegisterOptions = {}): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  if (import.meta.env.DEV && !options.force) return;

  /**
   * Register as soon as the document is ready — and register NOW if it already
   * is.
   *
   * Waiting only on `load` looked right and was wrong: when the bundle finishes
   * executing after that event has already fired, the listener is attached to an
   * event that will never come again, and the offline shell silently never
   * exists. That failure is invisible until the day somebody is in a basement,
   * which is the worst possible time to discover it.
   */
  const register = () => {
    void navigator.serviceWorker.register(`${BASE}sw.js`, { scope: BASE }).catch((error) => {
      // Degraded, not broken: the app still works online and the outbox still
      // works from the page. Not worth an error in the user's face — but it is
      // worth saying so somewhere a developer will look.
      console.warn('[qms360] The offline shell could not be installed.', error);
    });
  };

  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, { once: true });
  }

  // The worker cannot flush the queue itself: the access token and the merge
  // logic live here, in the page. It wakes us instead.
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data as { type?: string; path?: unknown } | undefined;
    if (data?.type === 'qms360:flush-outbox') {
      void flushOutbox().then((report) => options.onFlushed?.(report));
      return;
    }
    // A push notification was clicked while QMS360 was already open: go where
    // it points, in this window. The routes are hash routes, so this is a
    // hash change — and only ever to an in-app path.
    if (data?.type === 'qms360:open-path' && isAppPath(data.path)) {
      window.location.hash = data.path;
    }
  });
}

/** The same rule the worker applies: an in-app path, never another site. */
export function isAppPath(path: unknown): path is string {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && /^\/[A-Za-z0-9\-_/.]*$/.test(path);
}

/**
 * Ask the browser to flush the outbox once it has a network, even if QMS360 is
 * closed by then.
 *
 * Background Sync is not available everywhere — notably not on iOS — so this
 * returns whether the request was accepted. A `false` is not a failure: the
 * `online` listener in `sync.ts` covers the same ground whenever the app is
 * open, which on a phone used for site work is most of the time it matters.
 */
export async function requestBackgroundSync(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;

  try {
    const registration = (await navigator.serviceWorker.ready) as SyncCapableRegistration;
    if (!registration.sync) return false;
    await registration.sync.register(SYNC_TAG);
    return true;
  } catch {
    return false;
  }
}
